import { SelectInputData, SelectInputField } from "@src/components/deprecated/SelectInputField/SelectInputField";
import { TextInputField } from "@src/components/deprecated/TextInputField/TextInputField";
import { DateInput } from "@src/components/ui-components/Inputs/DateInput/DateInput";
import { Utilities } from "@utils/utilities";
import { t } from "i18next";
import { DataObjectFieldComparator, periods } from "../../DataObjects/DataObjectFieldComparator";
import { DataObjectType } from "../../DataObjects/DataObjectHelper";
import { DataObjectField, DecodeStringContext, InputElementContext } from "../DataObjectField";
import styles from "../DataObjectField.module.css";

export class FirebaseDataDateField<FirebaseDataType extends DataObjectType> extends DataObjectField<Date, FirebaseDataType> {
  override equalComparator = (a: Date | undefined, b: Date | undefined) => {
    return Utilities.isSameDate(a, b);
  };
  override singleInputElement = (value: any | undefined, setValue: (value: any | undefined) => void, context?: InputElementContext) => {
    if (context?.comparator === "within-period") {
      const options = periods.map((period) => new SelectInputData(period.key, period.label));
      return (
        <SelectInputField
          width={context?.minWidth}
          menuPortalTarget={context?.menuPortalTarget}
          defaultValue={value && options.find((option) => option.value === value)}
          options={options}
          onChange={(e) => e instanceof SelectInputData && setValue(e.value)}
          disabled={context?.disabled}
        />
      );
    }

    if (context?.comparator && (context.comparator === "rolling-back" || context.comparator === "rolling-forward")) {
      const stringValue = typeof value === "string" ? value : typeof value === "number" ? value.toString() : "";
      return (
        <TextInputField
          width={context?.width}
          height={context?.height}
          className={styles.textField}
          value={stringValue}
          placeholder={t("main.fillInAText")}
          onChange={(text) => setValue(text)}
          disabled={context?.disabled}
          trailingText={t("main.days")}
        />
      );
    }

    const dateValue = value instanceof Date ? value : null;
    return <DateInput onChange={setValue} date={dateValue} disabled={context?.disabled} style={{ width: context?.width }} />;
  };

  override getReadableStringRepresentation(value: Date): string {
    return Utilities.formatDate(value, "dd/mmmm/yyyy");
  }

  override encodeString = (value: Date) => value.toString();
  override decodeString = (value: string, context?: DecodeStringContext) => {
    for (const separator of ["/", "-", "."]) {
      const split = value.split(separator);
      if (split.length === 3) {
        const yearFirst = split[0].length === 4;
        const day = split[yearFirst ? 2 : 0];
        const month = split[1];
        const year = split[yearFirst ? 0 : 2];
        const result = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        if (!isNaN(result.getTime())) {
          return result;
        }
      }
    }

    if (context?.format === "excel") {
      value = value.replace(",", ".");

      // Check if value text is a number using regex
      const isNumber = /^\d+$/.test(value);
      if (!isNumber) {
        return undefined;
      }

      const numberValue = parseFloat(value);
      if (!isNaN(numberValue)) {
        // Converts from excel date to js date
        return new Date(Date.UTC(0, 0, numberValue - 1));
      }
      return undefined;
    }

    // This is a method to support within-period filters and comparators
    // We should refactor filters to not encode/decode values but instead use the raw value directly to avoid the as any
    if (typeof value === "string" && periods.find((period) => period.key === value)) {
      return value as any;
    }

    const result = new Date(value);
    if (!isNaN(result.getTime())) return result;

    return undefined;
  };

  override encode = (value: Date) => value;
  override decode = (value: any) => {
    if (value instanceof Date) return value;
    return typeof value === "string" ? this.decodeString(value) : Utilities.getDateFromTimestamp(value);
  };

  allComparators: DataObjectFieldComparator[] = [
    "equals",
    "not-equals",
    "includes",
    "greater-than",
    "greater-than-or-equal-to",
    "less-than",
    "less-than-or-equal-to",
    "is-known",
    "is-unknown",
    "within-period",
    "rolling-back",
    "rolling-forward",
  ];

  override isValueValid = (value: any): boolean => {
    if (value instanceof Date) return true;
    if (typeof value === "string" && periods.find((period) => period.key === value)) return true;
    return false;
  };
}
